import React, { ChangeEvent, FC, useCallback } from 'react';
import { FormGroup, FormText, Input, Label } from 'reactstrap';
import { RegistrationFlagModel } from '../../shared/registration/model';
import { useTranslation } from '../translations';
import { toggleElementInArray, useConvention } from '../utils';

interface AttendanceFlagsProps {
  readonly flags: RegistrationFlagModel[];
  readonly enabledFlags: number[];
  readonly readOnly?: boolean;
  readonly askForChildren?: boolean;
  readonly hasChildren?: boolean;
  readonly toggleChildren?: () => void;
  readonly onUpdate: (flags: number[]) => void;
}

export const AttendanceFlags: FC<AttendanceFlagsProps> = ({
  enabledFlags,
  flags,
  readOnly,
  askForChildren,
  hasChildren,
  onUpdate,
  toggleChildren,
}) => {
  const { ts } = useTranslation();
  const { childAgeThreshold } = useConvention();
  const onFlagChange = useCallback(
    ({ currentTarget }: ChangeEvent<HTMLInputElement>) => {
      if (!readOnly) {
        const flagId = Number.parseInt(currentTarget.value, 10);
        onUpdate(toggleElementInArray(enabledFlags, flagId));
      }
    },
    [readOnly, enabledFlags, onUpdate],
  );

  return (
    <>
      {flags.map((flag) => {
        const isEnabled = enabledFlags.includes(flag.id);
        const key = `flag${flag.id}`;
        return (
          <FormGroup check className="mt-10" key={flag.id}>
            <Input
              defaultChecked={isEnabled}
              disabled={readOnly}
              id={key}
              name="flags[]"
              onChange={onFlagChange}
              type="checkbox"
              value={flag.id}
            />
            <Label check for={`flag${flag.id}`}>
              {flag.description}
              {isEnabled ? <FormText color="muted">{flag.enabledDescription}</FormText> : null}
            </Label>
          </FormGroup>
        );
      })}
      {askForChildren && (
        <FormGroup check className="mt-10">
          <Input
            checked={hasChildren}
            id="registerChildren"
            onChange={toggleChildren}
            type="checkbox"
          />
          <Label check for="registerChildren">
            {ts('attendance_flags.id_like_to_register_under', childAgeThreshold)}
          </Label>
        </FormGroup>
      )}
    </>
  );
};
